// Busca de mesa da liga: pareamento pela divisão. Começa na mesma divisão e abre uma divisão para cima e para
// baixo a cada 20 s. Depois de 60 s, as vagas que faltam vão para bots. Quem espera há mais tempo puxa a mesa.
// Um trio fixo entra junto e ocupa um time inteiro; quem entra sozinho completa as vagas.
import { DIVISOES } from './divisoes.ts';

export const FAIXA_A_CADA_MS = 20_000;
export const BOTS_DEPOIS_MS = 60_000;

export interface Procurando {
  id: string; // usuário (sozinho) ou trio
  usuarios: string[]; // 1 ou 3
  divisao: number;
  desde: number;
  trio: string | null;
}

export interface MesaPareada {
  // lugares 0 a 5: time 0 nos pares (0, 2, 4), time 1 nos ímpares. null é vaga de bot.
  lugares: (string | null)[];
  trios: [string | null, string | null];
  comBot: boolean;
  entradas: Procurando[];
}

export interface TemposDaBusca { faixa: number; bots: number }
export const TEMPOS_DA_BUSCA: TemposDaBusca = { faixa: FAIXA_A_CADA_MS, bots: BOTS_DEPOIS_MS };

export function faixa(p: Procurando, agora: number, t: TemposDaBusca = TEMPOS_DA_BUSCA): number {
  return Math.floor(Math.max(0, agora - p.desde) / t.faixa);
}

// Nome da faixa aberta para quem espera há esperaMs: "Café com Leite" ou "Café com Leite a Malandro".
export function nomeDaFaixa(divisao: number, esperaMs: number, t: TemposDaBusca = TEMPOS_DA_BUSCA): string {
  const n = Math.floor(Math.max(0, esperaMs) / t.faixa), de = Math.max(0, divisao - n), ate = Math.min(DIVISOES.length - 1, divisao + n);
  return de === ate ? DIVISOES[de]!.nome : `${DIVISOES[de]!.nome} a ${DIVISOES[ate]!.nome}`;
}

// Se a divisão está na faixa aberta para a âncora. É a mesma conta do pareamento e da contagem da fila.
export function dentroDaFaixa(ancora: Procurando, divisao: number, agora: number, t: TemposDaBusca = TEMPOS_DA_BUSCA): boolean {
  return Math.abs(divisao - ancora.divisao) <= faixa(ancora, agora, t);
}

export interface ContagemDaFila {
  total: number; // pessoas procurando mesa agora: um trio fixo conta 3
  naFaixa: Map<string, number>; // por usuário: as outras pessoas na faixa aberta para ele, com os parceiros do trio
}

// A fila vista por quem procura, só em números. Agrupa pela divisão para não comparar todos com todos.
export function contarFila(fila: readonly Procurando[], agora: number, t: TemposDaBusca = TEMPOS_DA_BUSCA): ContagemDaFila {
  const porDivisao = new Map<number, number>();
  let total = 0;
  for (const p of fila) { total += p.usuarios.length; porDivisao.set(p.divisao, (porDivisao.get(p.divisao) ?? 0) + p.usuarios.length); }
  const naFaixa = new Map<string, number>();
  for (const p of fila) {
    let n = 0;
    for (const [d, pessoas] of porDivisao) if (dentroDaFaixa(p, d, agora, t)) n += pessoas;
    for (const u of p.usuarios) naFaixa.set(u, n - 1); // a própria entrada está na faixa: tira só quem recebe
  }
  return { total, naFaixa };
}

const LUGARES_DO_TIME = [[0, 2, 4], [1, 3, 5]] as const;

// Monta os dois times: trio fixo ocupa um time; os avulsos completam, equilibrando a soma das divisões.
function montar(escolhidos: Procurando[]): MesaPareada {
  const times: string[][] = [[], []], somas = [0, 0], trios: [string | null, string | null] = [null, null];
  for (const e of escolhidos.filter((x) => x.trio)) {
    const t = times[0]!.length === 0 ? 0 : 1;
    times[t] = [...e.usuarios]; somas[t] = somas[t]! + e.divisao * 3; trios[t] = e.trio;
  }
  for (const e of escolhidos.filter((x) => !x.trio).sort((a, b) => b.divisao - a.divisao)) {
    const livres = [0, 1].filter((t) => times[t]!.length < 3);
    const t = livres.length === 2 ? (somas[0]! <= somas[1]! ? 0 : 1) : livres[0]!;
    times[t]!.push(e.usuarios[0]!); somas[t] = somas[t]! + e.divisao;
  }
  const lugares: (string | null)[] = [null, null, null, null, null, null];
  times.forEach((us, t) => us.forEach((u, i) => { lugares[LUGARES_DO_TIME[t]![i]!] = u; }));
  return { lugares, trios, comBot: lugares.some((l) => l === null), entradas: escolhidos };
}

// Tira da fila as mesas que já dá para formar. A fila não muda: devolve as mesas e quem ficou.
export function parear(fila: readonly Procurando[], agora: number, t: TemposDaBusca = TEMPOS_DA_BUSCA): { mesas: MesaPareada[]; restam: Procurando[] } {
  const restam = [...fila].sort((a, b) => a.desde - b.desde);
  const mesas: MesaPareada[] = [];
  for (let i = 0; i < restam.length;) {
    const ancora = restam[i]!;
    const escolhidos: Procurando[] = [ancora];
    let vagas = 6 - ancora.usuarios.length, times = ancora.trio ? 1 : 0;
    for (const c of restam) {
      if (vagas === 0) break;
      if (escolhidos.includes(c) || !dentroDaFaixa(ancora, c.divisao, agora, t)) continue;
      // Um trio ocupa um time inteiro: no máximo dois trios e seis pessoas na mesa.
      if (c.usuarios.length > vagas || (c.trio && times >= 2)) continue;
      if (c.trio) times++;
      escolhidos.push(c);
      vagas -= c.usuarios.length;
    }
    if (vagas === 0 || agora - ancora.desde >= t.bots) {
      mesas.push(montar(escolhidos));
      for (const e of escolhidos) restam.splice(restam.indexOf(e), 1);
    } else i++;
  }
  return { mesas, restam };
}
